import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, ArrowLeft, FileText, Phone, FlaskConical, Bell, RefreshCw, Loader2, Trash2, Save } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PruebasTab } from "@/components/whatsapp/PruebasTab";
import { NotificacionesTab } from "@/components/whatsapp/NotificacionesTab";
import PhoneInput from "@/components/PhoneInput";
import useLicenseAccess from "@/hooks/useLicenseAccess";

const PROVIDER_STYLES = {
  gupshup: { label: "Gupshup", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  twilio: { label: "Twilio", className: "bg-blue-100 text-blue-700 border-blue-200" },
  evolution: { label: "Evolution", className: "bg-orange-100 text-orange-700 border-orange-200" },
  meta_cloud: { label: "Meta Cloud", className: "bg-purple-100 text-purple-700 border-purple-200" },
};

const STATUS_STYLES = {
  active: { label: "Activa", className: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  inactive: { label: "Inactiva", className: "bg-gray-500/20 text-gray-300 border-gray-500/30" },
  error: { label: "Error", className: "bg-red-500/20 text-red-300 border-red-500/30" },
};

const SCOPE_LABELS = {
  global: "Global",
  secondary: "Secundaria",
  development: "Desarrollo",
  product: "Producto",
};

const MODULE_OPTIONS = [
  { value: "", label: "Sin módulo" },
  { value: "notificaciones", label: "Notificaciones" },
  { value: "logicas", label: "Lógicas" },
  { value: "campanas", label: "Masivas" },
];

const moduleClass = {
  notificaciones: "bg-blue-500/20 text-blue-300",
  logicas: "bg-violet-500/20 text-violet-300",
  campanas: "bg-orange-500/20 text-orange-300",
};

const categoryClass = {
  MARKETING: "bg-pink-500/20 text-pink-300",
  UTILITY: "bg-cyan-500/20 text-cyan-300",
  AUTHENTICATION: "bg-amber-500/20 text-amber-300",
};


// ── Sub-components ──────────────────────────────────────────────────────
function HeaderInfo({ waba }) {
  const provCfg = PROVIDER_STYLES[waba.provider] || {
    label: waba.provider, className: "bg-gray-100 text-gray-700",
  };
  const statCfg = STATUS_STYLES[waba.status] || STATUS_STYLES.inactive;
  const scopeText =
    waba.scope?.type === "global"
      ? "Global"
      : waba.scope?.type === "secondary"
      ? "Secundaria"
      : `${SCOPE_LABELS[waba.scope?.type] || waba.scope?.type || "—"}${
          waba.scope?.ref_id ? `: ${waba.scope.ref_id}` : ""
        }`;

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-white">{waba.name || "—"}</h2>
        <Badge variant="outline" className={`font-medium ${provCfg.className}`}>
          {provCfg.label}
        </Badge>
        <Badge variant="outline" className={`font-medium ${statCfg.className}`}>
          {statCfg.label}
        </Badge>
        {waba.is_global_fallback && (
          <Badge variant="outline" className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30">
            Fallback global
          </Badge>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-400">
        <span>Scope: <span className="text-gray-200">{scopeText}</span></span>
        {waba.phone_number && (
          <span className="flex items-center gap-1">
            <Phone className="h-3 w-3" />
            <span className="font-mono text-gray-200">{waba.phone_number}</span>
          </span>
        )}
      </div>
    </div>
  );
}


function TemplateCard({ tpl, onAssignModule, savingModuleId, onDelete, canUseMassCampaigns = true }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(tpl.module_type || "");
  const isSaving = savingModuleId === tpl.id;

  return (
    <Card className="bg-zinc-950/80 border-white/10" data-testid={`waba-detail-tpl-${tpl.id}`}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-white truncate flex-1">
            {tpl.name || tpl.element_name || "—"}
          </p>
          <div className="flex flex-wrap gap-1 justify-end">
            {tpl.category && (
              <Badge className={`${categoryClass[tpl.category] || "bg-zinc-700 text-zinc-200"} border-0 text-[10px]`}>
                {tpl.category}
              </Badge>
            )}
            {tpl.module_type && (
              <Badge className={`${moduleClass[tpl.module_type] || "bg-zinc-700 text-zinc-200"} border-0 text-[10px]`}>
                {MODULE_OPTIONS.find((o) => o.value === tpl.module_type)?.label || tpl.module_type}
              </Badge>
            )}
          </div>
        </div>
        <div className="rounded-md border border-yellow-900/30 bg-[#ece5dd] p-2">
          <p className="text-[12px] text-zinc-900 line-clamp-3">{(tpl.body || "").slice(0, 240)}</p>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[10px] text-zinc-500">{tpl.language || tpl.content_sid || ""}</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-[11px] text-cyan-300 hover:text-cyan-200"
              onClick={() => setOpen((v) => !v)}
              data-testid={`waba-detail-tpl-toggle-${tpl.id}`}
            >
              {open ? "Cancelar" : "Asignar módulo"}
            </button>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-red-400 hover:text-red-300 disabled:opacity-50"
                    onClick={() => onDelete?.(tpl.id)}
                    disabled={isSaving}
                    data-testid={`waba-detail-tpl-delete-${tpl.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Eliminar plantilla</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        {open && (
          <div className="rounded-md border border-indigo-500/30 bg-indigo-950/40 p-2 space-y-2">
            <div className="grid grid-cols-2 gap-1">
              {MODULE_OPTIONS.map((opt) => {
                const isMass = opt.value === "campanas";
                const disabled = isMass && !canUseMassCampaigns;
                return (
                  <label
                    key={opt.value || "none"}
                    className={`flex items-center gap-1 text-xs ${
                      disabled ? "cursor-not-allowed text-zinc-500" : "cursor-pointer text-zinc-200"
                    }`}
                    title={disabled ? "Tu plan no incluye WhatsApp / Voice Blaster" : undefined}
                    data-testid={`waba-detail-tpl-mod-opt-${tpl.id}-${opt.value || "none"}`}
                  >
                    <input
                      type="radio"
                      name={`mod-${tpl.id}`}
                      value={opt.value}
                      checked={draft === opt.value}
                      onChange={() => !disabled && setDraft(opt.value)}
                      disabled={disabled}
                      className="accent-indigo-500 disabled:opacity-50"
                    />
                    {opt.label}
                    {disabled && <span className="text-[10px] text-zinc-500">(plan)</span>}
                  </label>
                );
              })}
            </div>
            <Button
              size="sm"
              className="h-7 w-full text-[11px] gap-1"
              onClick={() => onAssignModule(tpl.id, draft, () => setOpen(false))}
              disabled={isSaving || (draft === "campanas" && !canUseMassCampaigns)}
              data-testid={`waba-detail-tpl-save-${tpl.id}`}
            >
              {isSaving ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ── Evolution QR section (con auto-recovery en conflicto 403/already in use)
function EvolutionQRSection({ wabaId, onPhoneDetected }) {
  const [status, setStatus] = useState(null);
  const [qr, setQr] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  // Helper: tras detectar conexión "open", pedir el número al backend.
  const detectPhoneSilent = async () => {
    if (!wabaId) return;
    try {
      const r = await api.post(`/waba-accounts/${wabaId}/detect-phone`);
      const phone = r?.data?.phone_number;
      if (phone) onPhoneDetected?.(phone);
    } catch {
      // No-fatal: el admin puede ingresar el número manualmente.
    }
  };

  useEffect(() => {
    let cancelled = false;
    api
      .get("/whatsapp/status")
      .then((r) => {
        if (cancelled) return;
        setStatus(r.data);
        if (r.data?.connected) detectPhoneSilent();
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wabaId]);

  const tryConnect = async () => {
    const r = await api.post("/whatsapp/connect-qr");
    if (r.data?.already_connected) {
      const s = await api.get("/whatsapp/status");
      setStatus(s.data);
      setQr(null);
      detectPhoneSilent();
      toast.info("WhatsApp ya está conectado");
      return;
    }
    if (r.data?.qr_code) {
      setQr(r.data.qr_code);
      toast.success("Escanea el código QR");
      setTimeout(() => {
        api
          .get("/whatsapp/status")
          .then((rr) => {
            setStatus(rr.data);
            if (rr.data?.connected) {
              setQr(null);
              detectPhoneSilent();
            }
          })
          .catch(() => {});
      }, 15000);
      return;
    }
    // Sin QR ni conexión → fuerza el flujo de error.
    const err = new Error(r.data?.message || "No se pudo obtener el QR");
    err._noToken = true;
    throw err;
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await tryConnect();
    } catch (err) {
      const httpStatus = err?.response?.status;
      const detail = String(err?.response?.data?.detail || err?.message || "").toLowerCase();
      const conflict = httpStatus === 403 || detail.includes("already");
      if (conflict) {
        try {
          setReconnecting(true);
          toast.info("Reconectando instancia...");
          await api.post("/whatsapp/reset-instance");
          await tryConnect();
        } catch {
          toast.error("Error al conectar. Intenta de nuevo.");
        } finally {
          setReconnecting(false);
        }
      } else {
        toast.error("Error al conectar. Intenta de nuevo.");
      }
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.post("/whatsapp/disconnect");
      setStatus({ connected: false });
      setQr(null);
      toast.success("WhatsApp desconectado");
    } catch {
      toast.error("Error al desconectar");
    }
  };

  const busy = connecting || reconnecting;
  const isConnected = !!status?.connected;

  return (
    <div className="space-y-3" data-testid="waba-detail-evolution-qr-section">
      <div className="flex items-center gap-2">
        {isConnected ? (
          <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">Conectado</Badge>
        ) : (
          <Badge className="bg-gray-500/20 text-gray-300 border-gray-500/30">No conectado</Badge>
        )}
        {status?.phone_number && (
          <span className="text-xs font-mono text-gray-300">{status.phone_number}</span>
        )}
      </div>

      {qr && (
        <div
          className="p-4 rounded-lg bg-white flex items-center justify-center"
          data-testid="waba-detail-evolution-qr-img"
        >
          <img
            src={qr.startsWith("data:") ? qr : `data:image/png;base64,${qr}`}
            alt="QR WhatsApp"
            className="w-48 h-48"
          />
        </div>
      )}

      {reconnecting && (
        <p className="text-xs text-amber-300" data-testid="waba-detail-evolution-reconnecting">
          Reconectando instancia...
        </p>
      )}

      <div className="flex gap-2">
        {!isConnected ? (
          <Button
            onClick={handleConnect}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2"
            data-testid="waba-detail-evolution-connect-btn"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Conectar via QR
          </Button>
        ) : (
          <Button
            onClick={handleDisconnect}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:text-white"
            data-testid="waba-detail-evolution-disconnect-btn"
          >
            Desconectar
          </Button>
        )}
      </div>
    </div>
  );
}


function LoadingGrid() {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-40 rounded-lg" />
      ))}
    </div>
  );
}


// ── Evolution: editor del número conectado ──────────────────────────────
function EvolutionPhoneEditor({ waba, onUpdated }) {
  const [phone, setPhone] = useState(waba?.phone_number || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPhone(waba?.phone_number || "");
  }, [waba?.phone_number]);

  const handleSave = async () => {
    const cleaned = (phone || "").trim();
    if (!cleaned) {
      toast.error("Ingresa un número válido");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...waba, phone_number: cleaned };
      delete payload._id;
      await api.put(`/waba-accounts/${waba.id}`, payload);
      toast.success("Número de WhatsApp actualizado");
      onUpdated?.(cleaned);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar el número");
    } finally {
      setSaving(false);
    }
  };

  const dirty = (phone || "").trim() !== (waba?.phone_number || "").trim();

  return (
    <Card className="bg-gray-900/40 border border-gray-700" data-testid="waba-detail-evolution-phone-editor">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-2">
          <Phone className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-white">Número de WhatsApp conectado</p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Este es el número del teléfono físico conectado a esta instancia.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex-1">
            <PhoneInput
              value={phone}
              onChange={setPhone}
              placeholder="Número del celular que escaneó el QR"
              data-testid="waba-detail-evolution-phone-input"
            />
          </div>
          <Button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2 disabled:opacity-50"
            data-testid="waba-detail-evolution-phone-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


function EmptyState({ title, description }) {
  return (
    <Card className="border border-dashed border-gray-700 bg-gray-900/30">
      <CardContent className="flex flex-col items-center gap-2 p-8 text-center">
        <AlertCircle className="h-6 w-6 text-gray-500" />
        <p className="text-sm font-semibold text-gray-200">{title}</p>
        {description && <p className="max-w-md text-xs text-gray-400">{description}</p>}
      </CardContent>
    </Card>
  );
}


// ── Plantillas tab ──────────────────────────────────────────────────────
function PlantillasTabContent({ waba, resolvedConfigId }) {
  const [syncedTemplates, setSyncedTemplates] = useState([]);
  const [syncOpen, setSyncOpen] = useState(false);
  const [savingModuleId, setSavingModuleId] = useState("");
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  const { canAccess } = useLicenseAccess();
  const canUseMassCampaigns = canAccess("mass_campaigns");

  const provider = waba?.provider;
  const appId = waba?.credentials?.app_id;

  // ── Cargar plantillas guardadas en wa_templates ─────────────────────
  // Función reutilizable (load inicial + recarga tras sync) que lee
  // siempre desde la DB para asegurar que los docs tengan el id UUID local.
  const loadTemplates = useCallback(async () => {
    if (provider !== "gupshup" && provider !== "twilio") return;
    if (provider === "gupshup" && (resolvedConfigId === undefined || resolvedConfigId === null)) {
      return;
    }
    const params =
      provider === "gupshup"
        ? { provider: "gupshup", waba_id: resolvedConfigId, page_size: 100 }
        : { provider: "twilio", page_size: 100 };
    const r = await api.get("/whatsapp/templates/all", { params });
    const items =
      r.data?.items ||
      r.data?.templates ||
      (Array.isArray(r.data) ? r.data : []);
    // Ocultar plantillas archivadas (huérfanas borradas del lado Gupshup). El backend ya las excluye
    // por defecto; este filtro es defensa en profundidad para la vista de plantillas.
    const visible = items.filter((t) => String(t?.status || "").toUpperCase() !== "ARCHIVED");
    setSyncedTemplates(visible);
  }, [provider, resolvedConfigId]);

  // Hooks deben ejecutarse antes de cualquier early-return.
  useEffect(() => {
    let cancelled = false;
    if (provider !== "gupshup" && provider !== "twilio") {
      setLoadingTemplates(false);
      return;
    }
    if (provider === "gupshup") {
      if (resolvedConfigId === undefined) {
        // aún resolviendo — mantener loading
        return;
      }
      if (resolvedConfigId === null) {
        setLoadingTemplates(false);
        setSyncedTemplates([]);
        return;
      }
    }
    setLoadingTemplates(true);
    setFetchError(false);
    (async () => {
      try {
        await loadTemplates();
      } catch (err) {
        if (!cancelled) {
          setFetchError(true);
          setSyncedTemplates([]);
        }
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [provider, resolvedConfigId, loadTemplates]);

  // ── Early returns por provider/estado ───────────────────────────────
  if (provider === "evolution") {
    return (
      <div className="space-y-3" data-testid="waba-detail-evolution-qr">
        <Card className="bg-gray-900/40 border border-gray-700">
          <CardContent className="p-4 space-y-3">
            <p className="text-sm font-semibold text-white">Conexión WhatsApp via QR</p>
            <EvolutionQRSection
              wabaId={waba?.id}
              onPhoneDetected={(phone) => {
                if (typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent("waba:phone-detected", { detail: { wabaId: waba?.id, phone } }));
                }
              }}
            />
          </CardContent>
        </Card>
        <p className="text-xs text-gray-400">
          Evolution API envía mensajes de texto libre. Las plantillas HSM no aplican para este proveedor.
        </p>
      </div>
    );
  }
  if (provider === "meta_cloud") {
    return (
      <EmptyState
        title="Próximamente"
        description="La gestión de plantillas para Meta Cloud API estará disponible en una siguiente fase."
      />
    );
  }
  if (provider === "gupshup" && !appId && !waba?.gupshup_config_id) {
    return (
      <EmptyState
        title="Esta cuenta no tiene un número WABA vinculado"
        description="Verifica las credenciales en la configuración."
      />
    );
  }
  if (provider === "gupshup" && resolvedConfigId === null) {
    return (
      <EmptyState
        title="Esta cuenta no tiene un número WABA vinculado"
        description="Verifica las credenciales en la configuración."
      />
    );
  }
  if (provider === "gupshup" && resolvedConfigId === undefined) {
    return <LoadingGrid />;
  }
  if (loadingTemplates) {
    return <LoadingGrid />;
  }
  if (fetchError) {
    return (
      <EmptyState
        title="Error al cargar plantillas"
        description="Verifica tu conexión y vuelve a intentar."
      />
    );
  }

  const handleAssignModule = async (templateId, value, closeFn) => {
    setSavingModuleId(templateId);
    try {
      const moduleType = value || null;
      if (moduleType === "campanas" && !canUseMassCampaigns) {
        toast.error("Tu plan no incluye WhatsApp / Voice Blaster");
        return;
      }
      await api.put(`/whatsapp/templates/${templateId}/module`, { module_type: moduleType });
      setSyncedTemplates((prev) =>
        prev.map((t) => (t.id === templateId ? { ...t, module_type: moduleType } : t))
      );
      toast.success(moduleType ? "Módulo asignado" : "Módulo desasignado");
      closeFn?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar el módulo");
    } finally {
      setSavingModuleId("");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await api.delete(`/whatsapp/templates/${templateId}`);
      setSyncedTemplates((prev) => prev.filter((t) => t.id !== templateId));
      toast.success("Plantilla eliminada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar plantilla");
    }
  };

  return (
    <div className="space-y-3">
      {syncedTemplates.length === 0 ? (
        <Card className="border border-dashed border-gray-700 bg-gray-900/30" data-testid="waba-detail-tpl-empty">
          <CardContent className="flex flex-col items-center gap-3 p-12 text-center">
            <FileText className="h-8 w-8 text-gray-500" />
            <p className="text-sm font-semibold text-gray-200">Aún no has sincronizado plantillas</p>
            <p className="max-w-md text-xs text-gray-400">
              Selecciona qué plantillas remotas quieres usar con esta cuenta WABA.
            </p>
            <Button
              onClick={() => setSyncOpen(true)}
              className="mt-2 gap-2"
              data-testid="waba-detail-sync-btn"
            >
              <RefreshCw className="h-4 w-4" /> Sincronizar plantillas
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-400">
              {syncedTemplates.length} plantilla{syncedTemplates.length === 1 ? "" : "s"} sincronizada{syncedTemplates.length === 1 ? "" : "s"}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSyncOpen(true)}
              className="gap-2"
              data-testid="waba-detail-resync-btn"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Sincronizar
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {syncedTemplates.map((tpl) => (
              <TemplateCard
                key={tpl.id || tpl.template_id || tpl.content_sid}
                tpl={tpl}
                onAssignModule={handleAssignModule}
                savingModuleId={savingModuleId}
                onDelete={handleDeleteTemplate}
                canUseMassCampaigns={canUseMassCampaigns}
              />
            ))}
          </div>
        </>
      )}

      <SyncModal
        open={syncOpen}
        onClose={() => setSyncOpen(false)}
        provider={provider}
        resolvedConfigId={resolvedConfigId}
        wabaId={waba?.id}
        currentSelected={syncedTemplates}
        onSaved={async () => {
          try {
            await loadTemplates();
          } catch (err) {
            // si falla, mantenemos el estado previo; el toast del sync ya se mostró
          }
          setSyncOpen(false);
        }}
      />
    </div>
  );
}


// ── Sync modal ──────────────────────────────────────────────────────────
// Identificador único de un item remoto/guardado para tracking de selección.
const itemKey = (t) =>
  t?.template_id || t?.id || t?.content_sid || t?.sid || t?.elementName || t?.name;

function SyncModal({ open, onClose, provider, resolvedConfigId, wabaId, currentSelected, onSaved }) {
  const [state, setState] = useState({ status: "idle", items: [], error: null });
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  // Pre-checkear las que ya están guardadas (match por template_id/id/content_sid)
  useEffect(() => {
    if (open) {
      const presaved = new Set(
        (currentSelected || []).map((t) => itemKey(t)).filter(Boolean)
      );
      setSelected(presaved);
    }
  }, [open, currentSelected]);

  // Fetch remoto al abrir
  useEffect(() => {
    let cancelled = false;
    if (!open) return undefined;
    setState({ status: "loading", items: [], error: null });
    (async () => {
      try {
        let data;
        if (provider === "gupshup") {
          const r = await api.get("/whatsapp/gupshup/templates", {
            params: { config_id: resolvedConfigId },
          });
          data =
            r.data?.templates ||
            (Array.isArray(r.data) ? r.data : []);
        } else if (provider === "twilio") {
          if (!wabaId) {
            throw new Error("Falta waba_id para sincronizar plantillas Twilio");
          }
          const r = await api.get("/whatsapp/twilio/templates", {
            params: { waba_id: wabaId },
          });
          data =
            r.data?.templates ||
            (Array.isArray(r.data) ? r.data : []);
        } else {
          data = [];
        }
        if (!cancelled) setState({ status: "ok", items: data, error: null });
      } catch (err) {
        if (!cancelled) {
          setState({
            status: "error",
            items: [],
            error: err?.response?.data?.detail || err?.message || "No se pudieron cargar plantillas remotas",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, provider, resolvedConfigId, wabaId]);

  const toggleOne = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const allSelected =
    state.items.length > 0 && state.items.every((t) => selected.has(itemKey(t)));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(state.items.map((t) => itemKey(t)).filter(Boolean)));
  };

  const handleSave = async () => {
    const picked = state.items.filter((t) => selected.has(itemKey(t)));
    setSaving(true);
    try {
      if (provider === "gupshup") {
        await api.post("/whatsapp/gupshup/templates", {
          config_id: resolvedConfigId,
          templates: picked,
        });
      } else if (provider === "twilio") {
        await api.post("/whatsapp/twilio/templates/save", {
          waba_id: wabaId,
          templates: picked,
        });
      }
      toast.success("Plantillas sincronizadas");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al sincronizar plantillas");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent
        className="max-w-2xl bg-gray-900 border border-gray-700 text-gray-100"
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="waba-sync-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white">Sincronizar plantillas</DialogTitle>
          <DialogDescription className="text-gray-400">
            Selecciona las plantillas remotas que quieres mantener en esta cuenta WABA.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] overflow-y-auto">
          {state.status === "loading" && <LoadingGrid />}
          {state.status === "error" && (
            <EmptyState title="Error" description={state.error} />
          )}
          {state.status === "ok" && state.items.length === 0 && (
            <EmptyState
              title="No hay plantillas remotas"
              description="Verifica que el proveedor tenga plantillas aprobadas."
            />
          )}
          {state.status === "ok" && state.items.length > 0 && (
            <div className="space-y-2">
              <button
                type="button"
                className="text-xs text-cyan-300 hover:text-cyan-200"
                onClick={toggleAll}
                data-testid="waba-sync-toggle-all"
              >
                {allSelected ? "Deseleccionar todos" : "Seleccionar todos"}
              </button>
              <div className="space-y-1 rounded-md border border-gray-700 bg-gray-800/40 p-2">
                {state.items.map((tpl) => {
                  const k = itemKey(tpl);
                  return (
                    <label
                      key={k}
                      className="flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-gray-800/60"
                      data-testid={`waba-sync-item-${k}`}
                    >
                      <Checkbox
                        checked={selected.has(k)}
                        onCheckedChange={() => toggleOne(k)}
                        className="border-gray-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-100 truncate">
                          {tpl.name || tpl.elementName || tpl.element_name || tpl.friendly_name || k}
                        </p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {tpl.category && (
                          <Badge className={`${categoryClass[tpl.category] || "bg-zinc-700 text-zinc-200"} border-0 text-[10px]`}>
                            {tpl.category}
                          </Badge>
                        )}
                        {tpl.status && (
                          <Badge variant="outline" className="text-[10px] border-gray-600 text-gray-300">
                            {tpl.status}
                          </Badge>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            data-testid="waba-sync-cancel"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={state.status !== "ok" || saving}
            className="gap-2"
            data-testid="waba-sync-save"
          >
            {(saving || state.status === "loading") && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? "Guardando…" : `Guardar selección (${selected.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ── Main component ──────────────────────────────────────────────────────
export default function WabaDetailView({ waba, onBack }) {
  const [tab, setTab] = useState("plantillas");
  const [resolvedConfigId, setResolvedConfigId] = useState(undefined);
  const [localPhone, setLocalPhone] = useState(waba?.phone_number || "");
  // undefined = resolviendo, null = sin match / no aplica, string = match
  const navigate = useNavigate();

  const handleBack = useMemo(() => onBack || (() => {}), [onBack]);

  const provider = waba?.provider;
  const appId = waba?.credentials?.app_id;
  const directConfigId = waba?.gupshup_config_id;

  // Resolución compartida: corre una sola vez al montar (o cuando cambia la cuenta)
  // y se pasa a los 3 tabs evitando fetches duplicados.
  useEffect(() => {
    let cancelled = false;
    if (provider !== "gupshup") {
      setResolvedConfigId(null);
      return;
    }
    // Si la cuenta WABA ya trae gupshup_config_id, úsalo directo.
    if (directConfigId) {
      setResolvedConfigId(directConfigId);
      return;
    }
    if (!appId) {
      setResolvedConfigId(null);
      return;
    }
    setResolvedConfigId(undefined);
    (async () => {
      try {
        const { data } = await api.get("/whatsapp/gupshup/numbers");
        if (cancelled) return;
        const numbers = Array.isArray(data?.numbers) ? data.numbers : Array.isArray(data) ? data : [];
        const match = numbers.find((c) => c.app_id && c.app_id === appId);
        setResolvedConfigId(match?.id || null);
      } catch {
        if (!cancelled) setResolvedConfigId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [provider, appId, directConfigId]);

  // Escuchar el evento "waba:phone-detected" emitido por EvolutionQRSection
  // cuando detect-phone responde con un número válido tras conectar el QR.
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.wabaId === waba?.id && e.detail.phone) {
        setLocalPhone(e.detail.phone);
      }
    };
    window.addEventListener("waba:phone-detected", handler);
    return () => window.removeEventListener("waba:phone-detected", handler);
  }, [waba?.id]);

  if (!waba) return null;

  // ── Pruebas tab content ──
  let pruebasContent;
  if (provider === "evolution") {
    pruebasContent = (
      <div
        className="flex flex-col items-center justify-center p-8 text-center bg-gray-800/50 border border-gray-700 rounded-lg gap-4"
        data-testid="waba-detail-evolution-pruebas-card"
      >
        <FlaskConical className="h-10 w-10 text-gray-500" />
        <div>
          <h3 className="text-gray-200 font-medium mb-2">Pruebas con Evolution API</h3>
          <p className="text-gray-400 text-sm">
            Evolution envía mensajes de texto libre desde el Social Inbox.
            Para enviar un mensaje de prueba, ve al Social Inbox
            y busca o crea una conversación con el número destino.
          </p>
        </div>
        <Button
          variant="outline"
          className="border-gray-600 text-gray-300 hover:text-white"
          onClick={() => navigate("/ai-agent")}
          data-testid="waba-detail-evolution-pruebas-cta"
        >
          Ir a Conversaciones IA
        </Button>
      </div>
    );
  } else if (provider === "gupshup") {
    if (resolvedConfigId === undefined) {
      pruebasContent = <LoadingGrid />;
    } else if (resolvedConfigId) {
      pruebasContent = <PruebasTab initialWabaId={resolvedConfigId} />;
    } else {
      pruebasContent = (
        <EmptyState
          title="Esta cuenta no tiene un número WABA vinculado"
          description="Verifica las credenciales en la configuración."
        />
      );
    }
  } else {
    pruebasContent = <PruebasTab initialWabaId={waba?.id || null} />;
  }

  return (
    <div className="space-y-4" data-testid="waba-detail-view">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleBack}
        className="gap-2 text-gray-300 hover:text-white"
        data-testid="waba-detail-back"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver a cuentas
      </Button>

      <HeaderInfo waba={{ ...waba, phone_number: localPhone || waba?.phone_number }} />

      {provider === "evolution" && (
        <EvolutionPhoneEditor
          waba={{ ...waba, phone_number: localPhone || waba?.phone_number || "" }}
          onUpdated={(newPhone) => setLocalPhone(newPhone)}
        />
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-gray-800/60 border border-gray-700">
          <TabsTrigger
            value="plantillas"
            className="data-[state=active]:bg-gray-900 data-[state=active]:text-white text-gray-300 gap-1"
            data-testid="waba-detail-tab-plantillas"
          >
            <FileText className="h-3.5 w-3.5" /> Plantillas
          </TabsTrigger>
          <TabsTrigger
            value="pruebas"
            className="data-[state=active]:bg-gray-900 data-[state=active]:text-white text-gray-300 gap-1"
            data-testid="waba-detail-tab-pruebas"
          >
            <FlaskConical className="h-3.5 w-3.5" /> Pruebas
          </TabsTrigger>
          <TabsTrigger
            value="notificaciones"
            className="data-[state=active]:bg-gray-900 data-[state=active]:text-white text-gray-300 gap-1"
            data-testid="waba-detail-tab-notificaciones"
          >
            <Bell className="h-3.5 w-3.5" /> Notificaciones
          </TabsTrigger>
        </TabsList>

        <TabsContent value="plantillas" className="m-0">
          <PlantillasTabContent waba={waba} resolvedConfigId={resolvedConfigId} />
        </TabsContent>
        <TabsContent value="pruebas" className="m-0">
          {pruebasContent}
        </TabsContent>
        <TabsContent value="notificaciones" className="m-0">
          {provider === "evolution" ? (
            <div
              className="flex flex-col items-center justify-center p-8 text-center bg-gray-800/50 border border-gray-700 rounded-lg gap-4"
              data-testid="waba-detail-evolution-notif-card"
            >
              <Bell className="h-10 w-10 text-gray-500" />
              <div>
                <h3 className="text-gray-200 font-medium mb-2">Notificaciones con Evolution API</h3>
                <p className="text-gray-400 text-sm">
                  Evolution envía mensajes de texto libre, no plantillas HSM.
                  Para configurar notificaciones con Evolution, ve a
                  WhatsApp WABA → Notificaciones
                  y selecciona el proveedor Evolution (texto libre).
                </p>
              </div>
              <Button
                variant="outline"
                className="border-gray-600 text-gray-300 hover:text-white"
                onClick={() => navigate("/whatsapp?seccion=notificaciones")}
                data-testid="waba-detail-evolution-notif-cta"
              >
                Ir a Notificaciones
              </Button>
            </div>
          ) : (
            <NotificacionesTab
              initialWabaConfigId={
                (waba?.provider === 'gupshup')
                  ? (waba?.gupshup_config_id || null)
                  : null
              }
              wabaAccountId={waba?.id || null}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
